/**
 * Supabase Authentication Context
 * Provides authentication state and methods using Supabase Auth
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { User as SupabaseUser, Session, AuthError } from '@supabase/supabase-js';
import { db, supabase } from '../lib/supabase';
import { fetchWithInterceptors, sessionUtils } from '../utils/optimizedApiInterceptors';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Profile Cache Configuration
const PROFILE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
interface CachedProfile {
  profile: User;
  expiresAt: number;
}
let profileCache: CachedProfile | null = null;

// User type matching the application structure
const roleRank: Record<'superadmin' | 'admin' | 'company_admin' | 'agent' | 'user', number> = {
  superadmin: 3,
  admin: 2,
  company_admin: 2,
  agent: 1,
  user: 0,
};

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'user' | 'agent' | 'admin' | 'company_admin' | 'superadmin';
  roles: string[];
  orgId: string;
  organizationName?: string;
  plan?: string;
  isSuperAdmin: boolean;
  companyId: string; // Legacy compatibility
  can_view_breakdown?: boolean; // Permission to view cost breakdown details
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  session: Session | null;
  login: (email: string, password: string, turnstileToken?: string) => Promise<{ error?: string }>;
  loginWithRedirect: (options?: {redirectTo?: string}) => void;
  logout: (redirectTo?: string) => Promise<void>;
  signUp: (email: string, password: string, name: string, companyName?: string, turnstileToken?: string) => Promise<{ error?: string }>;
  getAccessToken: () => Promise<string>;
  refreshUserProfile: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error?: string }>;
  updatePassword: (newPassword: string) => Promise<{ error?: string }>;
  error: string | null;
}

const SupabaseAuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(SupabaseAuthContext);
  if (!context) {
    throw new Error('useAuth must be used within a SupabaseAuthProvider');
  }
  return context;
};

interface SupabaseAuthProviderProps {
  children: ReactNode;
}

export const SupabaseAuthProvider: React.FC<SupabaseAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isInitializedRef = useRef(false); // Track if initial auth check is done using ref

  // Convert Supabase user to application user format
  const loadUserProfile = async (supabaseUser: SupabaseUser, forceRefresh = false): Promise<User | null> => {
    const profileTimeoutMs = 22000; // 22 seconds timeout for profile loading (Railway cold start + buffer)

    // Check cache first unless force refresh
    if (!forceRefresh && profileCache && profileCache.expiresAt > Date.now()) {
      return profileCache.profile;
    }

    return Promise.race([
      loadUserProfileInternal(supabaseUser),
      new Promise<null>((_, reject) =>
        setTimeout(() => reject(new Error('Profile loading timeout')), profileTimeoutMs)
      )
    ]);
  };

  const loadUserProfileInternal = async (supabaseUser: SupabaseUser): Promise<User | null> => {
    try {

      // Prefer server-side profile (bypasses RLS and includes company context)
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000); // 20 second timeout for API call (Railway cold start)
        
        const resp = await fetchWithInterceptors(`${API_BASE_URL}/api/v1/auth/profile`, { 
          method: 'GET',
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        // Accept both 2xx success and 304 Not Modified as valid responses
        const isValidResponse = resp.ok || resp.status === 304;
        
        if (!isValidResponse) {
          console.warn('⚠️ Profile API returned error status:', resp.status);
          // Continue to fallback
        }
        
        if (isValidResponse) {
          const json = await resp.json();
          const p = json.user;
          const c = json.company;
          const m = json.membership;
          if (p && c && m) {
            
            const appUser: User = {
              id: p.id,
              email: p.email,
              name: p.full_name || (p.email ? p.email.split('@')[0] : ''),
              role: m.role, // Use the actual role from membership
              roles: [m.role], // Use the actual role from membership
              orgId: c.id,
              organizationName: c.name,
              plan: c.subscription_status === 'active' ? 'starter' : 'trial',
              isSuperAdmin: !!m.permissions?.isSuperAdmin,
              companyId: c.id,
              can_view_breakdown: p.can_view_breakdown || m.permissions?.canViewBreakdown || false,
            };
            setError(null);

            // Cache the profile
            profileCache = {
              profile: appUser,
              expiresAt: Date.now() + PROFILE_CACHE_TTL
            };

            return appUser;
          }
        }
      } catch (e) {
        // Continue to fallback
      }

      // Fallback: client-side DB (subject to RLS)
      const { profile, error: profileError } = await db.getUserProfile(supabaseUser.id);

      if (profile && !profileError) {
        setError(null);

        // Cache the profile from fallback
        profileCache = {
          profile,
          expiresAt: Date.now() + PROFILE_CACHE_TTL
        };

        return profile;
      }

      if (profileError?.code === 'PGRST116') {
        // Create missing profile
        const { profile: newProfile, error: createError } = await db.createUserProfile(
          supabaseUser.id,
          supabaseUser.email || '',
          supabaseUser.user_metadata?.full_name || supabaseUser.user_metadata?.name
        );
        if (createError) {
          console.error('Failed to create profile:', createError);
          setError('Failed to create user profile. Please contact support.');
          return null;
        }
        setError(null);
        return {
          id: newProfile.id,
          email: newProfile.email,
          name: newProfile.full_name || newProfile.email.split('@')[0],
          role: 'user',
          roles: ['user'],
          orgId: '',
          organizationName: undefined,
          plan: 'trial',
          isSuperAdmin: false,
          companyId: '',
          can_view_breakdown: false
        };
      }

      console.error('Error loading profile:', profileError);
      setError('Failed to load user profile. Please try refreshing.');
      return null;
    } catch (error) {
      console.error('Error loading user profile:', error);
      setError('Authentication error. Please try again.');
      return null;
    }
  };

  // Initialize auth state
  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout;

    // Check active session with intelligent caching
    const initAuth = async () => {
      try {

        // Pre-warm the session cache for faster auth responses
        await sessionUtils.preWarmCache();

        // Set a maximum timeout for the entire auth initialization
        timeoutId = setTimeout(() => {
          if (mounted && !isInitializedRef.current) {
            isInitializedRef.current = true;
            setIsLoading(false);
          }
        }, 25000); // 25 second timeout to handle Railway cold start

        const { data: { session: activeSession }, error: sessionError } = await supabase.auth.getSession();

        if (!mounted) return; // Component unmounted

        if (sessionError) {
          console.error('Session error:', sessionError);
          setError('Failed to restore session');
          isInitializedRef.current = true;
          setIsLoading(false);
          return;
        }

        if (activeSession?.user) {
          setSession(activeSession);

          try {
            // Load profile with timeout protection and caching
            const profile = await Promise.race([
              loadUserProfile(activeSession.user),
              new Promise<null>((_, reject) =>
                setTimeout(() => reject(new Error('Profile load timeout')), 22000) // 22s to handle Railway cold start
              )
            ]);
            
            if (mounted) {
              setUser(prev => {
                if (!prev || !profile) return profile;
                if (prev.id !== profile.id) return profile;
                const prevRank = roleRank[prev.role as keyof typeof roleRank] ?? -1;
                const nextRank = roleRank[profile.role as keyof typeof roleRank] ?? -1;
                // Prevent privilege downgrade
                return nextRank < prevRank ? prev : profile;
              });
            }
          } catch (profileError) {
            console.error('Profile loading failed during init; keeping existing user:', profileError);
            if (mounted) {
              // Do not degrade privileges with a fallback user. Keep existing user state.
              isInitializedRef.current = true;
              setIsLoading(false);
            }
          }
        } else {
          if (mounted) {
            isInitializedRef.current = true;
            setIsLoading(false);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setError('Authentication initialization failed');
        }
      } finally {
        if (mounted) {
          isInitializedRef.current = true;
          setIsLoading(false);
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }
    };

    initAuth();

    // Listen for auth changes with intelligent caching
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {

      if (!mounted) return; // Component unmounted

      // Skip INITIAL_SESSION since initAuth() already handles initial load
      if (event === 'INITIAL_SESSION') {
        return;
      }

      if ((event === 'SIGNED_IN' || event === 'USER_UPDATED') && session?.user) {
        setSession(session);

        // Skip profile loading if we already have user data to reduce loading screens
        if (user?.id === session.user.id) {
          return;
        }

        try {
          // Use loadUserProfile's internal timeout (10s) instead of adding another timeout here
          const profile = await loadUserProfile(session.user);

          if (mounted) {
            setUser(prev => {
              if (!prev || !profile) return profile;
              if (prev.id !== profile.id) return profile;
              const prevRank = roleRank[prev.role as keyof typeof roleRank] ?? -1;
              const nextRank = roleRank[profile.role as keyof typeof roleRank] ?? -1;
              return nextRank < prevRank ? prev : profile;
            });
            // Only set loading to false if we're already initialized (not during initial load)
            if (isInitializedRef.current) {
              setIsLoading(false);
            }
          }
        } catch (error) {
          // Only warn if we don't have any user data (i.e., this is a real problem)
          // If we already have user state, the timeout is not critical - just use cached data
          if (!user && import.meta.env.DEV) {
            console.warn('⚠️ Profile loading failed in auth state change for:', session.user.email, '- Error:', error instanceof Error ? error.message : error);
          }
          if (mounted) {
            // Keep existing user data and stop loading to prevent endless loading states
            // Only set loading to false if we're already initialized
            if (isInitializedRef.current) {
              setIsLoading(false);
            }
          }
        }
      } else if (event === 'SIGNED_OUT') {
        // Clear session cache on sign out
        sessionUtils.clearCache();
        setSession(null);
        setUser(null);
        setIsLoading(false);
      } else if (event === 'TOKEN_REFRESHED' && session) {
        // Token refresh doesn't need profile reload
        setSession(session);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  // Login with email and password
  const login = React.useCallback(async (email: string, password: string, turnstileToken?: string): Promise<{ error?: string }> => {
    try {
      setError(null);
      // Don't modify isLoading here - login component manages its own loading state

      // TEMPORARILY DISABLED: Turnstile verification
      // if (turnstileToken) {
      //   try {
      //     const verifyResponse = await fetch(`${API_BASE_URL}/api/verify-turnstile`, {
      //       method: 'POST',
      //       headers: { 'Content-Type': 'application/json' },
      //       body: JSON.stringify({ turnstileToken }),
      //     });

      //     if (!verifyResponse.ok) {
      //       const verifyData = await verifyResponse.json();
      //       const verifyError = verifyData.error || 'CAPTCHA verification failed';
      //       console.warn('⚠️ CAPTCHA verification failed, but continuing (non-strict mode):', verifyError);
      //       // setError(verifyError);
      //       // return { error: verifyError };
      //     }
      //   } catch (verifyError) {
      //     console.error('❌ Turnstile verification error:', verifyError);
      //     console.warn('⚠️ Continuing despite CAPTCHA error (non-strict mode)');
      //     // const errorMessage = 'CAPTCHA verification failed. Please try again.';
      //     // setError(errorMessage);
      //     // return { error: errorMessage };
      //   }
      // }

      const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });

      if (signInError) {
        console.error('❌ Sign in error:', signInError);
        const errorMessage = signInError.message || 'Failed to sign in';
        console.error('❌ Error message:', errorMessage);
        setError(errorMessage);
        return { error: errorMessage };
      }

      if (data.user && data.session) {
        setSession(data.session);

        // Load user profile without timeout (let it fail properly if needed)
        const profile = await loadUserProfile(data.user);
        setUser(profile);

        if (!profile) {
          return { error: 'Failed to load user profile' };
        }
      }

      return {};
    } catch (error: unknown) {
      console.error('Login error:', error);
      const errorMessage = error?.message || 'An unexpected error occurred';
      setError(errorMessage);
      return { error: errorMessage };
    }
  }, []);

  // Sign up new user
  const signUp = React.useCallback(async (
    email: string,
    password: string,
    name: string,
    companyName?: string
  ): Promise<{ error?: string }> => {
    const trimmedEmail = email.trim();
    const trimmedName = name.trim();
    const trimmedCompany = companyName?.trim() ?? '';

    if (!trimmedCompany) {
      const errorMessage = 'Company name is required to complete signup.';
      setError(errorMessage);
      return { error: errorMessage };
    }

    try {
      setError(null);
      // Don't modify isLoading here - signup component manages its own loading state

      const signupUrl = `${API_BASE_URL || ''}/api/v1/auth/signup`;
      const response = await fetch(signupUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: trimmedEmail,
          password,
          fullName: trimmedName,
          companyName: trimmedCompany
        })
      });

      let result: {data?: {session?: {access_token?: string}}} = {};
      try {
        result = await response.json();
      } catch (parseError) {
        // Ignore JSON parse errors and rely on status
      }

      if (!response.ok) {
        const code = result?.code;
        let errorMessage = result?.message || 'Failed to create account.';

        if ((code === 'AUTH_ERROR' && /already registered/i.test(result?.message)) || code === 'USER_EXISTS') {
          errorMessage = 'An account with this email already exists. Please sign in instead.';
        } else if (code === 'MISSING_COMPANY_NAME') {
          errorMessage = 'Please provide your company name to continue.';
        } else if (code === 'MISSING_FIELDS') {
          errorMessage = 'Please fill out all required fields and try again.';
        } else if (typeof result?.message === 'string' && /duplicate key|profiles_email_key/i.test(result.message)) {
          errorMessage = 'This email is already in use. Please sign in or use a different email.';
        }

        setError(errorMessage);
        return { error: errorMessage };
      }

      setError(null);
      return {};
    } catch (error: unknown) {
      const errorMessage = error?.message || 'An unexpected error occurred';
      setError(errorMessage);
      return { error: errorMessage };
    }
  }, []);

  // Logout - optionally redirect to a specific URL after cleanup (default: '/')
  const logout = React.useCallback(async (redirectTo = '/') => {
    try {
      console.log('🚪 Logout started...');
      setError(null);

      // Clear caches immediately
      sessionUtils.clearCache();
      profileCache = null;

      // Clear state first
      setSession(null);
      setUser(null);

      console.log('🧹 Clearing Supabase session with scope: global');
      
      // CRITICAL FIX: Use 'global' scope to sign out from ALL sessions/devices
      // This ensures the session is invalidated server-side, not just locally
      const { error: signOutError } = await supabase.auth.signOut({ scope: 'global' });
      
      if (signOutError) {
        console.error('❌ Supabase signOut error:', signOutError);
        // Continue with cleanup even if signOut fails
      } else {
        console.log('✅ Supabase signOut successful');
      }

      // NUCLEAR OPTION: Clear ALL localStorage to prevent any restoration
      // This is more aggressive but ensures no leftover session data
      console.log('🧹 Clearing ALL localStorage...');
      const keysToPreserve = ['theme', 'cookie-consent', 'cookie-consent-timestamp'];
      const preservedValues: Record<string, string> = {};
      
      // Preserve non-auth data
      keysToPreserve.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) {
          preservedValues[key] = value;
        }
      });
      
      // Clear everything
      localStorage.clear();
      
      // Restore preserved values
      Object.entries(preservedValues).forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });
      
      console.log('✅ localStorage cleared (preserved: theme, cookies)');

      // Clear session storage completely
      console.log('🧹 Clearing sessionStorage...');
      sessionStorage.clear();
      console.log('✅ sessionStorage cleared');

      // Clear IndexedDB (Supabase might use it for session storage)
      try {
        console.log('🧹 Clearing IndexedDB...');
        const databases = await window.indexedDB.databases();
        const supabaseDbs = databases.filter(db => db.name?.includes('supabase'));
        
        for (const db of supabaseDbs) {
          if (db.name) {
            console.log(`🗑️ Deleting IndexedDB: ${db.name}`);
            window.indexedDB.deleteDatabase(db.name);
          }
        }
        console.log('✅ IndexedDB cleared');
      } catch (e) {
        console.warn('⚠️ Could not clear IndexedDB:', e);
      }

      console.log('🔄 Redirecting...');
      
      // Use window.location.replace to prevent back button from restoring session
      // This is more aggressive than href and clears the history entry
      window.location.replace(redirectTo);
      
    } catch (error: unknown) {
      console.error('💥 Logout error:', error);
      setError(error?.message || 'Failed to logout');
      
      // Emergency cleanup even on error - use nuclear option
      console.log('🚨 Emergency cleanup...');
      try {
        localStorage.clear();
        sessionStorage.clear();
        console.log('✅ Emergency cleanup complete');
      } catch (e) {
        console.error('💥 Emergency cleanup failed:', e);
      }
      
      // Force redirect even if there's an error
      console.log('🔄 Force redirecting after error...');
      window.location.replace(redirectTo);
    }
  }, []);

  // Login with redirect (for OAuth providers)
  const loginWithRedirect = React.useCallback((options?: {redirectTo?: string}) => {
    // For now, redirect to login page
    // In future, can implement OAuth providers
    window.location.href = '/login';
  }, []);

  // Get access token
  const getAccessToken = React.useCallback(async (): Promise<string> => {
    if (!session) {
      throw new Error('No active session');
    }

    // Check if token needs refresh
    const expiresAt = session.expires_at;
    const now = Math.floor(Date.now() / 1000);

    if (expiresAt && expiresAt < now) {
      // Token expired, refresh it
      const { data: { session: newSession }, error } = await supabase.auth.refreshSession();
      if (error || !newSession) {
        throw new Error('Failed to refresh token');
      }
      setSession(newSession);
      return newSession.access_token;
    }

    return session.access_token;
  }, [session]);

  // Refresh user profile
  const refreshUserProfile = React.useCallback(async () => {
    try {
      if (!session?.user) {
        throw new Error('No active session');
      }

      // Clear cache and force refresh
      profileCache = null;

      const profile = await loadUserProfile(session.user, true);
      setUser(profile);
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
    }
  }, [session]);

  // Reset password
  const resetPassword = React.useCallback(async (email: string): Promise<{ error?: string }> => {
    try {
      setError(null);
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email);

      if (resetError) {
        const errorMessage = resetError.message || 'Failed to send reset email';
        setError(errorMessage);
        return { error: errorMessage };
      }

      return {};
    } catch (error: unknown) {
      const errorMessage = error?.message || 'An unexpected error occurred';
      setError(errorMessage);
      return { error: errorMessage };
    }
  }, []);

  // Update password
  const updatePassword = React.useCallback(async (newPassword: string): Promise<{ error?: string }> => {
    try {
      setError(null);
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });

      if (updateError) {
        const errorMessage = updateError.message || 'Failed to update password';
        setError(errorMessage);
        return { error: errorMessage };
      }

      return {};
    } catch (error: unknown) {
      const errorMessage = error?.message || 'An unexpected error occurred';
      setError(errorMessage);
      return { error: errorMessage };
    }
  }, []);

  const value: AuthContextType = React.useMemo(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    session,
    login,
    loginWithRedirect,
    logout,
    signUp,
    getAccessToken,
    refreshUserProfile,
    resetPassword,
    updatePassword,
    error
  }), [user, isLoading, session, login, loginWithRedirect, logout, signUp, getAccessToken, refreshUserProfile, resetPassword, updatePassword]);

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  );
};

export default SupabaseAuthContext;
